import { useCallback, useEffect, useRef, useState } from 'react';

import usePageVisibility from './usePageVisibility';

export const RECORDING_CAP_SECONDS = 90;
export const CAP_WARNING_SECONDS = 75;
const MIN_TAKE_MS = 2000;

const MIME_TYPE_PREFERENCE = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export type VoiceTake = {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
  objectUrl: string;
};

type MicError = 'denied' | 'unavailable';

type VoiceRecorderStatus = 'idle' | 'starting' | 'recording';

export const isRecordingSupported = () =>
  !!window.MediaRecorder && !!navigator.mediaDevices?.getUserMedia;

const pickMimeType = () =>
  MIME_TYPE_PREFERENCE.find((mimeType) => MediaRecorder.isTypeSupported(mimeType));

// Mic failure is browser-global, not per-question; remembered across remounts so
// later questions go straight to typing instead of failing the mic again.
let lastMicError: MicError | null = null;

export const forgetMicError = () => {
  lastMicError = null;
};

const useVoiceRecorder = () => {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [micError, setMicErrorState] = useState<MicError | null>(lastMicError);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [tooShort, setTooShort] = useState(false);
  const [take, setTake] = useState<VoiceTake | null>(null);

  const setMicError = useCallback((error: MicError) => {
    lastMicError = error;
    setMicErrorState(error);
  }, []);

  const isSupported = isRecordingSupported();

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const intervalRef = useRef<number>();
  const finishedRef = useRef(false);
  const assembledRef = useRef(false);
  const mountedRef = useRef(true);

  const stopStreamTracks = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
  };

  const assembleTake = useCallback(() => {
    if (assembledRef.current) return;
    assembledRef.current = true;
    window.clearInterval(intervalRef.current);
    stopStreamTracks();

    const durationMs = Date.now() - startedAtRef.current;
    const chunks = chunksRef.current;
    const recordedMimeType = recorderRef.current?.mimeType;
    chunksRef.current = [];
    recorderRef.current = null;
    streamRef.current = null;

    if (!mountedRef.current) return;
    setElapsedSeconds(0);

    if (durationMs < MIN_TAKE_MS || chunks.length === 0) {
      setTooShort(true);
      setStatus('idle');
      return;
    }

    const mimeType = recordedMimeType || chunks[0].type || 'audio/webm';
    const blob = new Blob(chunks, { type: mimeType });
    setTake({
      blob,
      mimeType,
      durationSeconds: Math.min(RECORDING_CAP_SECONDS, Math.round(durationMs / 1000)),
      objectUrl: URL.createObjectURL(blob),
    });
    setStatus('idle');
  }, []);

  const finishRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || finishedRef.current) return;
    finishedRef.current = true;
    window.clearInterval(intervalRef.current);
    stopStreamTracks();
    if (recorder.state === 'inactive') {
      assembleTake();
      return;
    }
    recorder.stop();
  }, [assembleTake]);

  const start = useCallback(async () => {
    if (status !== 'idle' || !isSupported || micError || take) return;
    setTooShort(false);
    setStatus('starting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      const errorName = error instanceof DOMException ? error.name : '';
      setMicError(
        errorName === 'NotAllowedError' || errorName === 'SecurityError' ? 'denied' : 'unavailable',
      );
      setStatus('idle');
      return;
    }

    streamRef.current = stream;
    if (!mountedRef.current) {
      stopStreamTracks();
      return;
    }

    let recorder: MediaRecorder;
    try {
      const mimeType = pickMimeType();
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      stopStreamTracks();
      setMicError('unavailable');
      setStatus('idle');
      return;
    }

    chunksRef.current = [];
    finishedRef.current = false;
    assembledRef.current = false;
    recorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = assembleTake;
    recorder.onerror = finishRecording;
    stream.getAudioTracks().forEach((track) => track.addEventListener('ended', finishRecording));

    startedAtRef.current = Date.now();
    try {
      recorder.start(1000);
    } catch {
      stopStreamTracks();
      recorderRef.current = null;
      streamRef.current = null;
      setMicError('unavailable');
      setStatus('idle');
      return;
    }
    setStatus('recording');

    intervalRef.current = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsedSeconds(Math.min(elapsed, RECORDING_CAP_SECONDS));
      if (elapsed >= RECORDING_CAP_SECONDS) finishRecording();
    }, 250);
  }, [status, isSupported, micError, take, setMicError, assembleTake, finishRecording]);

  const redo = useCallback(() => setTake(null), []);

  const isVisible = usePageVisibility();
  useEffect(() => {
    if (!isVisible && status === 'recording') finishRecording();
  }, [isVisible, status, finishRecording]);

  useEffect(() => {
    if (!take) return undefined;
    return () => URL.revokeObjectURL(take.objectUrl);
  }, [take]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      window.clearInterval(intervalRef.current);
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      stopStreamTracks();
    };
  }, []);

  return {
    status,
    isSupported,
    micError,
    elapsedSeconds,
    tooShort,
    take,
    start,
    stop: finishRecording,
    redo,
  };
};

export default useVoiceRecorder;
